import React, { useEffect } from 'react';
import {Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import './App.css';
import Landing from './pages/Landing';
import Login from './pages/UserLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import PatientDashboardLayout from './patient/PatientDashboardLayout';
import NewRequest from './patient/NewRequest';
import RequestsList from './patient/RequestsList';
import EditRequest from './patient/EditRequest';
import PatientRequestDetails from './patient/PatientRequestDetails';
import PatientProfile from './patient/PatientProfile';
import { isLogged, isLoggedUnsecuredToken, logout, isAdminLogged, getUser, checkTokenExpiration } from './services/helpers';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminHospitals from './admin/pages/AdminHospitals';
import AdminPatients from './admin/pages/AdminPatients';
import AdminRequests from './admin/pages/AdminRequests';
import AdminSystemUsers from './admin/pages/AdminSystemUsers';
import AdminNewUser from './admin/pages/AdminNewUser';
import AdminUserDetails from './admin/pages/AdminUserDetails';
import AdminPayments from './admin/pages/AdminPayments';
import AdminPaymentDetails from './admin/pages/AdminPaymentDetails';
import AdminPayouts from './admin/pages/AdminPayouts';
import AdminPayoutDetails from './admin/pages/AdminPayoutDetails';
import AdminDisputes from './admin/pages/AdminDisputes';
import AdminDisputeDetails from './admin/pages/AdminDisputeDetails';
import AdminReports from './admin/pages/AdminReports';
import AdminAudit from './admin/pages/AdminAudit';
import Profile from './admin/pages/Profile';
import AdminHospitalDetails from './admin/pages/AdminHospitalDetails';
import AdminPatientDetails from './admin/pages/AdminPatientDetails';
import AdminRequestDetails from './admin/pages/AdminRequestDetails';
import PatientSignup from './pages/PatientSignup';
import HospitalSignup from './pages/HospitalSignup';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import ContactUs from './pages/ContactUs';
import BlogPost from './pages/BlogPost';
import HospitalDashboardLayout from './hospital/HospitalDashboardLayout';
import HospitalDashboard from './hospital/HospitalDashboard';
import HospitalProfile from './hospital/HospitalProfile';
import HospitalRequestsList from './hospital/HospitalRequestsList';
import HospitalRequestDetails from './hospital/HospitalRequestDetails';
import HospitalWallet from './hospital/HospitalWallet';
// import Down from './dashboard/pages/Down';
function App() {
  
  // Check token expiration periodically (every 5 minutes)
  useEffect(() => {
    const checkExpiration = () => {
      checkTokenExpiration()
    }
    
    // Check immediately
    checkExpiration()
    
    // Check every 5 minutes
    const interval = setInterval(checkExpiration, 5 * 60 * 1000)
    
    return () => clearInterval(interval)
  }, [])
  
  window.onclose = (e) => {
    logout()
  }
  
  const PrivateRoute = () => {
    const logged = isLogged()
    if(logged){
      return <Outlet/>
    }else{
      return <Navigate to={'/login'} replace />
    }
  }

  const LoggedRoute = () => {
    const logged = isLogged()
    if(logged){
      const user = getUser()
      if(user?.role === 'HOSPITAL'){
        return <Navigate to={'/hospital/dashboard'} replace />
      } else {
      return <Navigate to={'/dashboard'} replace />
      }
    }else{
      return <Outlet/>
    }
  }

  const AdminPrivateRoute = () => {
    const logged = isAdminLogged()
    if(logged){
      return <Outlet/>
    }else{
      return <Navigate to={'/admin'} replace />
    }
  }
  return (
      <Routes> 
        <Route exact path="/" element={<Landing/>} />
        <Route exact path="privacy-policy" element={<PrivacyPolicy/>} />
        <Route exact path="terms-of-service" element={<TermsOfService/>} />
        <Route exact path="cookie-policy" element={<CookiePolicy/>} />
        <Route exact path="contact" element={<ContactUs/>} />
        <Route exact path="blog/:slug" element={<BlogPost/>} />
        <Route element={<LoggedRoute/>}>
          <Route exact path="login" element={<Login/>} />
          <Route exact path="forgot-password" element={<ForgotPassword/>} />
          <Route exact path="reset-password/:userId/:token" element={<ResetPassword/>} />
          <Route exact path="verify-email/:userId/:token" element={<VerifyEmail/>} />
          <Route exact path="signup/patient" element={<PatientSignup/>} />
          <Route exact path="signup/hospital" element={<HospitalSignup/>} />
          <Route exact path="payment/success" element={<PaymentSuccess/>} />
          <Route exact path="payment/failed" element={<PaymentFailed/>} />
        </Route>
        <Route element={<PrivateRoute/>}>
          <Route path="dashboard" element={<PatientDashboardLayout/>}>
            <Route index element={<Navigate to="requests" replace />} />
            <Route path="requests" element={<RequestsList/>} />
            <Route path="requests/new" element={<NewRequest/>} />
            <Route path="requests/:postId" element={<PatientRequestDetails/>} />
            <Route path="requests/:postId/edit" element={<EditRequest/>} />
            <Route path="profile" element={<PatientProfile/>} />
          </Route>
          <Route path="hospital" element={<HospitalDashboardLayout/>}>
            <Route path="dashboard" element={<HospitalDashboard/>} />
            <Route path="requests" element={<HospitalRequestsList/>} />
            <Route path="requests/:postId" element={<HospitalRequestDetails/>} />
            <Route path="wallet" element={<HospitalWallet/>} />
            <Route path="profile" element={<HospitalProfile/>} />
          </Route>
        </Route>
        <Route exact path="admin" element={<AdminLogin/>} />
        <Route element={<AdminPrivateRoute/>}>
          <Route path="admin/app" element={<AdminLayout/>}>
            <Route index element={<Navigate to="hospitals" replace />} />
            <Route path="hospitals" element={<AdminHospitals/>} />
            <Route path="hospitals/:userId" element={<AdminHospitalDetails/>} />
            <Route path="patients" element={<AdminPatients/>} />
            <Route path="patients/:userId" element={<AdminPatientDetails/>} />
        <Route path="requests" element={<AdminRequests/>} />
        <Route path="requests/:postId" element={<AdminRequestDetails/>} />
        <Route path="payments" element={<AdminPayments/>} />
        <Route path="payments/:paymentId" element={<AdminPaymentDetails/>} />
        <Route path="payouts" element={<AdminPayouts/>} />
        <Route path="payouts/:payoutId" element={<AdminPayoutDetails/>} />
        <Route path="disputes" element={<AdminDisputes/>} />
        <Route path="disputes/:disputeId" element={<AdminDisputeDetails/>} />
        <Route path="reports" element={<AdminReports/>} />
        <Route path="system-users" element={<AdminSystemUsers/>} />
            <Route path="system-users/new" element={<AdminNewUser/>} />
            <Route path="system-users/:adminId" element={<AdminUserDetails/>} />
            <Route path="audit" element={<AdminAudit/>} />
            <Route path="profile" element={<Profile/>} />
          </Route>
        </Route>
      </Routes>
  );
}

export default App;
